import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { getTrackBackground, Range } from 'react-range';
import { useDebouncedCallback } from 'use-debounce';
import styles from './FilterRangeInput.module.scss';
import { SliderNumberInput } from './SliderNumberInput';
import { SliderThumb } from '@hub/common/FilterSliderInput/FilterSliderInput';

export type FilterRangeInputProps = {
  id: string;
  values: Array<number | null>; // null values are allowed to indicate an open end search
  label?: string;
  unit?: string;
  min: number | null; // null will handle the case of an open lower end
  max: number | null; // null will handle the case of an open upper end
  onChange: (values: Array<number | null>) => void;
};

/**
 *
 * @deprecated please use FilterRangeInput from @hub/category/Filters/components/ui instead
 * @returns
 */
export const FilterRangeInput = ({ id, values, label, unit, min, max, onChange }: FilterRangeInputProps) => {
  const [sliderValues, setSliderValues] = useState(values);
  const [lowerInputValue, setLowerInputValue] = useState(values[0]?.toString() || null);
  const [higherInputValue, setHigherInputValue] = useState(values[1]?.toString() || null);

  useEffect(() => {
    setSliderValues(values);
    setLowerInputValue(values[0]?.toString() || null);
    setHigherInputValue(values[1]?.toString() || null);
  }, [values]);
  const labelId = `${id}-label`;

  const debouncedOnChange = useDebouncedCallback((currentValues: Array<number | null>) => {
    onChange(currentValues);
  }, 500);

  // handle values out of range (min, max)
  useEffect(() => {
    const currentValues = [sliderValues[0], sliderValues[1]];
    let sendUpdate = false;
    if (min !== null && currentValues[0] !== null && currentValues[0] < min) {
      currentValues[0] = min;
      sendUpdate = true;
    }
    if (max !== null && currentValues[0] !== null && currentValues[0] > max) {
      currentValues[0] = max;
      sendUpdate = true;
    }

    if (min !== null && currentValues[1] !== null && currentValues[1] < min) {
      currentValues[1] = min;
      sendUpdate = true;
    }
    if (max !== null && currentValues[1] !== null && currentValues[1] > max) {
      currentValues[1] = max;
      sendUpdate = true;
    }

    if (sendUpdate || values[0] !== currentValues[0] || values[1] !== currentValues[1]) {
      debouncedOnChange(currentValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderValues, min, max, debouncedOnChange]);

  const onLowerInputNumberChange = (val: string) => {
    // show whatever number the user has entered inside the input field as long as it has focus
    // but normalize the value for the slider and for passing outside of the component
    const valNumber = parseInt(val, 10);

    setLowerInputValue(val);
    if (valNumber === null || valNumber === undefined) {
      // do nothing
    } else if (Number.isNaN(valNumber)) {
      setSliderValues([null, sliderValues[1]]);
    } else if (min !== null && valNumber < min) {
      setSliderValues([min, sliderValues[1]]);
    } else if (sliderValues[1] !== null && valNumber > sliderValues[1]) {
      setSliderValues([sliderValues[1], sliderValues[1]]);
    } else if (sliderValues[1] !== null) {
      setSliderValues([valNumber, sliderValues[1]]);
      debouncedOnChange([valNumber, sliderValues[1]]);
    }
  };

  const onHigherInputNumberChange = (val: string) => {
    // show whatever number the user has entered inside the input field as long as it has focus
    // but normalize the value for the slider and for passing outside of the component
    const valNumber = parseInt(val, 10);

    setHigherInputValue(val);
    if (valNumber === null || valNumber === undefined) {
      // do nothing
    } else if (Number.isNaN(valNumber)) {
      setSliderValues([sliderValues[0], null]);
    } else if (max !== null && valNumber > max) {
      setSliderValues([sliderValues[0], max]);
    } else if (sliderValues[0] !== null && valNumber < sliderValues[0]) {
      setSliderValues([sliderValues[0], sliderValues[0]]);
    } else if (sliderValues[0] !== null) {
      setSliderValues([sliderValues[0], valNumber]);
      debouncedOnChange([sliderValues[0], valNumber]);
    }
  };

  const resetHigherInputNumberToValidValue = (val: string) => {
    // as soon as the input loses focus, also reset the number to a valid value
    // (min or max) when the user entered number is exceeding the valid range
    const valNumber = parseInt(val, 10) || (max !== null ? max : null);
    if (max !== null && valNumber !== null && valNumber > max) {
      setHigherInputValue(max.toString());
      setSliderValues([sliderValues[0], max]);
      onChange([sliderValues[0], max]);
    } else if (valNumber !== null && sliderValues[0] !== null && valNumber < sliderValues[0]) {
      setHigherInputValue(lowerInputValue);
      setSliderValues([sliderValues[0], sliderValues[0]]);
      onChange([sliderValues[0], sliderValues[0]]);
    } else if (valNumber !== null) {
      setHigherInputValue(val);
      setSliderValues([sliderValues[0], valNumber]);
      onChange([sliderValues[0], valNumber]);
    }
  };

  const resetLowerInputNumberToValidValue = (val: string) => {
    // as soon as the input loses focus, also reset the number to a valid value
    // (min or max) when the user entered number is exceeding the valid range
    const valNumber = parseInt(val, 10) || (min !== null ? min : null);
    if (valNumber !== null && sliderValues[1] !== null && valNumber > sliderValues[1]) {
      setLowerInputValue(higherInputValue);
      setSliderValues([sliderValues[1], sliderValues[1]]);
      onChange([sliderValues[1], sliderValues[1]]);
    } else if (min !== null && valNumber !== null && valNumber < min) {
      setLowerInputValue(min.toString());
      setSliderValues([min, sliderValues[1]]);
      onChange([min, sliderValues[1]]);
    } else if (valNumber !== null) {
      setLowerInputValue(val);
      setSliderValues([valNumber, sliderValues[1]]);
      onChange([valNumber, sliderValues[1]]);
    }
  };

  const sliderMin = min !== null ? min : null;
  const sliderMax = max !== null ? max : null;

  return min !== max || (min === null && max === null) ? (
    <>
      {label && (
        <label id={labelId} htmlFor={id} className="block mb-6">
          {label}
        </label>
      )}
      <div className={classNames('flex')}>
        <div className={classNames('overflow-hidden', 'mb-5', 'w-5/12', 'relative')}>
          <SliderNumberInput
            id={`${id}_lowerInputNumber`}
            unit={unit}
            min={sliderMin}
            max={sliderMax}
            unitWidth={8}
            onChange={onLowerInputNumberChange}
            onBlur={resetLowerInputNumberToValidValue}
            value={lowerInputValue || null}
          />
        </div>
        <div className={classNames('w-2/12', 'mt-4')}>
          <hr className={classNames('mt-4', 'w-6', styles.rangeDivider)} />
        </div>
        <div className={classNames('overflow-hidden', 'mb-5', 'w-5/12', 'relative')}>
          <SliderNumberInput
            id={`${id}_higherInputNumber`}
            unit={unit}
            min={sliderMin}
            max={sliderMax}
            unitWidth={8}
            onChange={onHigherInputNumberChange}
            onBlur={resetHigherInputNumberToValidValue}
            value={higherInputValue || null}
          />
        </div>
      </div>
      {min !== null &&
      max !== null &&
      sliderValues.filter(sliderValue => typeof sliderValue === 'number').length === 2 ? (
        <div className="flex items-stretch mb-2 relative">
          <Range
            data-testid={id}
            values={sliderValues}
            min={min}
            max={max}
            // we expect react-range and useState to work
            onChange={
              /* istanbul ignore next */ val => {
                setLowerInputValue(val[0]?.toString());
                setHigherInputValue(val[1]?.toString());
                setSliderValues(val);
              }
            }
            onFinalChange={
              /* istanbul ignore next */ newValues => {
                onChange(newValues);
              }
            }
            renderTrack={({ props, children }) => (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {sliderMin !== null && sliderMax !== null ? (
                  <div
                    style={{
                      ...props.style,
                      borderRadius: '2px',
                      height: '6px',
                      width: '100%',
                      background: getTrackBackground({
                        values: sliderValues,
                        colors: ['#DDDDDD', '#666666', '#DDDDDD'],
                        min: sliderMin,
                        max: sliderMax,
                        rtl: false,
                      }),
                    }}
                    ref={props.ref}
                    onMouseDown={props.onMouseDown}
                    onTouchStart={props.onTouchStart}
                  >
                    {children}
                  </div>
                ) : null}
              </>
            )}
            renderThumb={({ props: thumbProps, isDragged, index }) => (
              <SliderThumb
                labelledById={labelId}
                key={index}
                index={index}
                options={thumbProps}
                isDragging={isDragged}
                value={sliderValues[index]}
                unit={unit ?? ''}
              />
            )}
          />
        </div>
      ) : null}
    </>
  ) : null;
};
